/**
 * Terrain coordinate mapping — the single, committed world-unit ↔ heightmap
 * mapping (DR-021 / AD-200, executing AD-146 step d; E3.1.S1).
 *
 * World units are metres on the flat plane; +x is east, +z is south (the
 * PathPoint convention). Rasters are row-major int16 samples: the pixel COLUMN
 * grows east (+x), the pixel ROW grows south (+z). The tile grid is square:
 * tile (tx, tz) — integers, possibly negative — covers the HALF-OPEN world
 * rect
 *
 *   [origin.x + tx·S, origin.x + (tx+1)·S) × [origin.z + tz·S, origin.z + (tz+1)·S)
 *
 * where S = tileSizePx · TERRAIN_METERS_PER_PIXEL metres. A query exactly on a
 * set's outer max edge clamps inward by convention (evaluation-side; the
 * placement consumer lands in E3.1.S3).
 *
 * AD-146 Risks 3/6 (binding): this mapping — the constant, the orientation and
 * the origin semantics — is defined ONCE. Changing any of it after content
 * references tile values is equivalent to a world-seed change and requires a
 * formal owner-approved AD; it must never be changed casually. The per-set
 * locked generation parameters (seed, upstream pin, origin, tileSizePx) live at
 * tools/fixtures/terrain-source/<setId>/params.locked.json (created by the
 * E3.1.S2 bake tool the moment the first set exists).
 *
 * Pure and framework-free (AD-122): no Node/browser globals; safe for the
 * validator, the offline tools, and the boot-time placement consumer alike.
 * Elevation ships in the client content pack (AD-408 — client-pack.ts
 * sub-projects tiles to `tx`/`tz`/`elevation`); the climate rasters and the wire
 * (AD-125) stay untouched. The sampling law lives in terrain-sample.ts.
 */
/** Heightmap resolution: metres of world per raster pixel (AD-146 — frozen; see header). */
export const TERRAIN_METERS_PER_PIXEL = 30;
/**
 * Footprint, in heightmap pixels (TERRAIN_METERS_PER_PIXEL² each), from which an
 * open-world zone must bind a terrain set (q-large-zones-terrain-coverage, owner
 * 2026-10-23: real elevation in the worlds people log into). 8 px = 7,200 m²:
 * the Measured Yard (72 × 112 m, ~9 px) is in; a town square (60 × 60 m, 4 px)
 * spans only 2–3 pixels a side — a tilted plane, not relief — and stays flat.
 */
export const TERRAIN_REQUIRED_MIN_AREA_PX = 8;
/** Hard sanity cap on the number of tiles a single bounds rect may span (validator guard). */
export const TERRAIN_MAX_RANGE_TILES = 4096;
/**
 * Map a world-unit position to its heightmap tile + intra-tile pixel (AD-146
 * Risk 6's `worldUnitsToHeightmapPixel`). Floor semantics throughout, so
 * negative world coordinates resolve to negative tile indices with in-range
 * pixel indices — no modulo sign surprises.
 */
export function worldUnitsToHeightmapPixel(x, z, set) {
    const gpx = Math.floor((x - set.origin.x) / set.metersPerPixel);
    const gpz = Math.floor((z - set.origin.z) / set.metersPerPixel);
    const tx = Math.floor(gpx / set.tileSizePx);
    const tz = Math.floor(gpz / set.tileSizePx);
    return { tx, tz, px: gpx - tx * set.tileSizePx, pz: gpz - tz * set.tileSizePx };
}
/**
 * The tile range a bounds rect spans under a set's grid, treating the rect as
 * HALF-OPEN on its max edges (a max edge exactly on a tile boundary does NOT
 * pull in the next tile — the matching evaluation-side clamp is documented in
 * the header). Used by the validator's coverage cross-check: every tile in this
 * range must be present in the bound set.
 */
export function terrainTileRange(bounds, set) {
    const S = set.metersPerPixel * set.tileSizePx;
    const txMin = Math.floor((bounds.minX - set.origin.x) / S);
    const tzMin = Math.floor((bounds.minZ - set.origin.z) / S);
    // Half-open max edge: back off an aligned edge to the tile that CONTAINS the
    // rect's interior. (maxX strictly > minX per the biome bounds contract, so
    // the range never inverts.)
    const txMax = openEdgeTile(bounds.maxX - set.origin.x, S);
    const tzMax = openEdgeTile(bounds.maxZ - set.origin.z, S);
    return { txMin, txMax, tzMin, tzMax };
}
/** Tile index containing the interior side of a half-open max edge at offset d. */
function openEdgeTile(d, S) {
    const t = Math.floor(d / S);
    return d === t * S ? t - 1 : t;
}
/** Number of tiles in a range (for the TERRAIN_MAX_RANGE_TILES guard). */
export function terrainTileRangeCount(range) {
    return (range.txMax - range.txMin + 1) * (range.tzMax - range.tzMin + 1);
}
/**
 * Exact padded base64 length of a tile raster layer: tileSizePx² little-endian
 * int16 samples ⇒ 2·tileSizePx² bytes ⇒ 4·ceil(bytes/3) base64 characters.
 * Pure string arithmetic so the browser-safe validator never decodes (full
 * byte-level checks are the E3.1.S2 bake tool's job — the AD-135 division of
 * labour).
 */
export function terrainLayerBase64Length(tileSizePx) {
    const bytes = 2 * tileSizePx * tileSizePx;
    return 4 * Math.ceil(bytes / 3);
}
/** Base64 charset shape (standard alphabet, optional trailing padding). */
export const TERRAIN_LAYER_BASE64_RE = /^[A-Za-z0-9+/]+={0,2}$/;
//# sourceMappingURL=terrain.js.map